// Catalog of canonical analytics events and their per-provider routing. Add a
// new event by (1) declaring an ANALYTICS_EVENT_* constant and (2) adding an
// entry to ANALYTICS_EVENT_CATALOG that lists which providers should fire it
// and how the payload maps for each. Providers absent from an entry are
// silently skipped — e.g., view_item_list goes to Google only because Meta has
// no native equivalent.


export const ANALYTICS_EVENT_PAGE_VIEW = 'page_view';
export const ANALYTICS_EVENT_VIEW_ITEM = 'view_item';
export const ANALYTICS_EVENT_VIEW_ITEM_LIST = 'view_item_list';
export const ANALYTICS_EVENT_ADD_TO_CART = 'add_to_cart';
export const ANALYTICS_EVENT_REMOVE_FROM_CART = 'remove_from_cart';
export const ANALYTICS_EVENT_VIEW_CART = 'view_cart';
export const ANALYTICS_EVENT_BEGIN_CHECKOUT = 'begin_checkout';
export const ANALYTICS_EVENT_ADD_SHIPPING_INFO = 'add_shipping_info';
export const ANALYTICS_EVENT_ADD_PAYMENT_INFO = 'add_payment_info';
export const ANALYTICS_EVENT_PURCHASE = 'purchase';
// A buylist is the store buying inventory FROM a customer — it is not store
// revenue, so it must NOT reuse `purchase` (which would inflate GA4/Meta
// revenue and conversion reports). Tracked as a distinct custom event instead.
export const ANALYTICS_EVENT_BUYLIST_SUBMIT = 'buylist_submit';


export type AnalyticsEventName =
	| typeof ANALYTICS_EVENT_PAGE_VIEW
	| typeof ANALYTICS_EVENT_VIEW_ITEM
	| typeof ANALYTICS_EVENT_VIEW_ITEM_LIST
	| typeof ANALYTICS_EVENT_ADD_TO_CART
	| typeof ANALYTICS_EVENT_REMOVE_FROM_CART
	| typeof ANALYTICS_EVENT_VIEW_CART
	| typeof ANALYTICS_EVENT_BEGIN_CHECKOUT
	| typeof ANALYTICS_EVENT_ADD_SHIPPING_INFO
	| typeof ANALYTICS_EVENT_ADD_PAYMENT_INFO
	| typeof ANALYTICS_EVENT_PURCHASE
	| typeof ANALYTICS_EVENT_BUYLIST_SUBMIT;


export type AnalyticsEventPayload = Record<string, unknown>;

export type AnalyticsProviderId = 'google' | 'meta';

export interface AnalyticsProviderRoute {
	// Provider-side event name. Google generally matches the canonical name;
	// Meta uses CamelCase ("AddToCart", "Purchase", "PageView").
	name: string;
	// Optional payload remap when the provider expects a different shape than
	// the canonical Google/GA4 ecommerce shape we author against.
	map?: (payload: AnalyticsEventPayload) => AnalyticsEventPayload;
}

export type AnalyticsEventEntry = Partial<Record<AnalyticsProviderId, AnalyticsProviderRoute>>;


// Canonical payloads are authored in GA4's ecommerce shape ({ currency, value,
// items: [{ item_id, item_name, price, quantity, ... }] }). Meta consumes a
// different shape, so commerce events run through this mapper.
type CommerceItem = { item_id?: string; quantity?: number };

const toMetaCommercePayload = (data: AnalyticsEventPayload): AnalyticsEventPayload => {
	const items: CommerceItem[] = Array.isArray(data.items) ? (data.items as CommerceItem[]) : [];
	return {
		currency: data.currency,
		value: data.value,
		content_type: 'product',
		content_ids: items.map(i => i.item_id).filter(Boolean),
		contents: items.map(i => ({ id: i.item_id, quantity: i.quantity || 1 })),
		num_items: items.length,
	};
};


export const ANALYTICS_EVENT_CATALOG: Record<AnalyticsEventName, AnalyticsEventEntry> = {
	[ANALYTICS_EVENT_PAGE_VIEW]: { 
		google: { name: 'page_view' },
		meta: { name: 'PageView' } 
	},
	[ANALYTICS_EVENT_VIEW_ITEM]: { 
		google: { name: 'view_item' },
		meta: { name: 'ViewContent', map: toMetaCommercePayload } 
	},
	[ANALYTICS_EVENT_VIEW_ITEM_LIST]: { 
		google: { name: 'view_item_list' } 
	},
	[ANALYTICS_EVENT_ADD_TO_CART]: { 
		google: { name: 'add_to_cart' },
		meta: { name: 'AddToCart', map: toMetaCommercePayload } 
	},
	[ANALYTICS_EVENT_REMOVE_FROM_CART]: { 
		google: { name: 'remove_from_cart' }
	},
	[ANALYTICS_EVENT_VIEW_CART]: { 
		google: { name: 'view_cart' } 
	},
	[ANALYTICS_EVENT_BEGIN_CHECKOUT]: { 
		google: { name: 'begin_checkout' },
		meta: { name: 'InitiateCheckout', map: toMetaCommercePayload } 
	},
	[ANALYTICS_EVENT_ADD_SHIPPING_INFO]: { 
		google: { name: 'add_shipping_info' } 
	},
	[ANALYTICS_EVENT_ADD_PAYMENT_INFO]: { 
		google: { name: 'add_payment_info' },
		meta: { name: 'AddPaymentInfo', map: toMetaCommercePayload }
	},
	[ANALYTICS_EVENT_PURCHASE]: {
		google: { name: 'purchase' },
		meta: { name: 'Purchase', map: toMetaCommercePayload }
	},
	// Google only: keep buylist submissions out of Meta's commerce/revenue
	// reporting. Buylist is inventory procurement, not a sale.
	[ANALYTICS_EVENT_BUYLIST_SUBMIT]: {
		google: { name: 'buylist_submit' }
	},
};
